import * as Sentry from "@sentry/react";

import { getFilterStateFromFilter } from "shared/api/utils";
import { randomID, splitIgnoringDoubleQuotes } from "shared/utils";

import {
  ALL_FILTER_SEPARATOR,
  ANY_FILTER_SEPARATOR,
  DEFAULT_FILTER_BUILDER_STATE,
} from "features/ui/Filters/FilterBuilder/constants";
import { AnyAll } from "features/ui/Filters/FilterBuilder/FilterGroup/AnyAllSelect";
import {
  FilterGroupState,
  FilterRowState,
} from "features/ui/Filters/FilterBuilder/types";
import { FilterOperator } from "features/ui/Filters/types";
import { getAttributesFromFilter } from "features/ui/Filters/utils";

/**
 * Filter string represents a group if it is wrapped with ().
 * - ignores empty space before/after the group.
 */
export const isFilterGroup = (str: string): boolean =>
  /^\(.*\)$/.test(str.trim());

/**
 * Removes outer parenthesis () from the string if they exist in pair.
 * - if they don't exist in pair, it returns the original string.
 * - if there's multiple matching parenthesis on the outside, it removes all of them
 * - make sure to ignore parenthesis inside double quotes
 */
export const removeOuterParenthesis = (str: string): string => {
  if (!isFilterGroup(str)) {
    return str;
  }

  str = str.trim();

  let count = 0;
  let inQuotes = false;

  const countOuterParentheses = (i: number) => {
    if (str[i] === '"') {
      inQuotes = !inQuotes;
    }

    if (!inQuotes) {
      if (str[i] === "(") {
        count++;
      } else if (str[i] === ")") {
        count--;
        if (count === 0 && i !== str.length - 1) {
          return str; // If there are more characters after closing parenthesis, return the original string
        }
      }
    }
  };

  // Check if the parentheses are balanced
  for (let i = 0; i < str.length; i++) {
    const newStr = countOuterParentheses(i);
    if (newStr) return newStr;
  }

  // Remove all outer pairs of balanced parentheses
  while (str.length > 2 && str[0] === "(" && str[str.length - 1] === ")") {
    str = str.slice(1, -1).trim();
    count = 0;
    inQuotes = false;
    for (let i = 0; i < str.length; i++) {
      const newStr = countOuterParentheses(i);
      if (newStr) return newStr;
    }
  }

  return str;
};

/**
 * If it has || (but NOT within "" or within other brackets) then it is an "any" group
 * - ie. 'signal=in:"A"|"B",model=eq:"XYZ",(signal=in:"C"|"D"||mileage=gt:"10000"||(make=eq:"Chevy",model=eq:"XXX"))' is still an "all" group.
 */
export const getGroupFilterAnyAll = (str: string): AnyAll => {
  const origString = str.trim();
  const splitByOr = splitIgnoringDoubleQuotes(
    removeOuterParenthesis(origString),
    ANY_FILTER_SEPARATOR
  );
  // we were able to split by top-level "||"
  if (splitByOr.length > 1) {
    return "any";
  }

  return "all";
};

export const hasMixedTopLevelDelimiters = (str: string) =>
  splitIgnoringDoubleQuotes(removeOuterParenthesis(str), ANY_FILTER_SEPARATOR)
    .length > 1 &&
  splitIgnoringDoubleQuotes(removeOuterParenthesis(str), ALL_FILTER_SEPARATOR)
    .length > 1;

// We do not support "eq" / "neq" as an operator in the UI, so we convert it to "in" / "nin"
const handleOperatorConversion = (
  operator: FilterOperator,
  values: string[]
): FilterOperator => {
  if (operator === FilterOperator.EQUALS) {
    if (values.length === 1) {
      if (values[0] === "true") {
        return FilterOperator.IS_TRUE;
      }

      if (values[0] === "false") {
        return FilterOperator.IS_FALSE;
      }
    }

    return FilterOperator.IN;
  }

  if (operator === FilterOperator.NOT_EQUALS) {
    return FilterOperator.NOT_IN;
  }

  return operator;
};

/**
 * Transforms filter query string to FilterGroupState state.
 * This is the reverse of getFiltersQuery.
 */
export const filterBuilderQueryToFilterBuilderState = (
  filterBuilderString: string | undefined
): FilterGroupState | undefined => {
  if (!filterBuilderString) return;

  let rootGroup: FilterGroupState | undefined = {
    ...DEFAULT_FILTER_BUILDER_STATE,
    children: [],
  };

  const parseGroup = (str: string, parentGroup: FilterGroupState): void => {
    const strWithoutOuterParenthesis = removeOuterParenthesis(str);
    const groupAnyAll = getGroupFilterAnyAll(strWithoutOuterParenthesis);
    parentGroup.anyAll = groupAnyAll;

    if (hasMixedTopLevelDelimiters(str)) {
      console.warn(
        `Invalid filter string: mixed delimiters ${strWithoutOuterParenthesis}`
      );
      rootGroup = undefined;

      return;
    }

    // now split by top-level "||" or "," based on groupAnyAll
    const splitBySeparator = splitIgnoringDoubleQuotes(
      strWithoutOuterParenthesis,
      groupAnyAll === "any" ? ANY_FILTER_SEPARATOR : ALL_FILTER_SEPARATOR
    );

    // loop through children and if it's a group, then recursively parse it
    splitBySeparator.forEach((childFilter) => {
      if (isFilterGroup(childFilter)) {
        const currentChildGroupAnyAll = getGroupFilterAnyAll(childFilter);

        // Create a new group for the current level
        const currentGroup: FilterGroupState = {
          ...DEFAULT_FILTER_BUILDER_STATE,
          id: `group-${randomID()}`,
          anyAll: currentChildGroupAnyAll,
          children: [],
        };

        parentGroup.children.push(currentGroup);

        // recursively parse the child group passing the current group as the parent
        parseGroup(childFilter, currentGroup);

        return;
      }

      // if it's not a group, then it's a row - add it as a child of the current group
      // TODO: can we get rid of these two utils somehow? They are only used here.
      const filters = getFilterStateFromFilter(childFilter);
      const attribute = getAttributesFromFilter(filters)[0];
      if (!attribute || !filters[attribute]) {
        console.warn("Invalid filter child, skipping:", childFilter);

        return;
      }

      // Convert "eq" to "in" & "neq" to "nin" because we do not support "eq" as an operator in the UI
      const operator = handleOperatorConversion(
        filters[attribute].operator,
        filters[attribute].values
      );

      const row: FilterRowState = {
        type: "row",
        id: `row-${randomID()}`,
        attribute,
        operator,
        values: filters[attribute].values,
      };

      parentGroup.children.push(row);
    });
  };

  try {
    parseGroup(filterBuilderString, rootGroup);
  } catch (e) {
    Sentry.captureException(e);
    console.error("Error parsing filter string", e, filterBuilderString);

    return;
  }

  return rootGroup;
};

/**
 * Depth-first search for the first row (at any nesting level) whose
 * `attribute` matches, returning the row or null. Used to locate the parent
 * row of an advanced (EXISTS-encoded) relation filter.
 */
export const findRowByAttribute = (
  filterGroup: FilterGroupState<FilterRowState>,
  attribute: string
): FilterRowState | null => {
  for (const child of filterGroup.children) {
    if (child.type === "row" && child.attribute === attribute) {
      return child;
    }

    if (child.type === "group") {
      const found = findRowByAttribute(
        child as FilterGroupState<FilterRowState>,
        attribute
      );
      if (found) return found;
    }
  }

  return null;
};

/**
 * Reads the relation value out of an advanced (EXISTS-encoded) filter row and
 * builds the dotted by-asset-age birthday path `parent.relationValue.dateChild`
 * (e.g. `lineExits.Final Line.exitTime`). The parent row's value is encoded as
 * `[<relationValueAttribute>=in:"Some Value",<dateChildAttribute>=inlast:1m]`;
 * `onlyReturnLineName` returns just the extracted relation value instead of the
 * composite path. Returns "" when no matching row/value is present.
 */
export const extractAdvancedFilterRelationInfo = (
  filterGroup: FilterGroupState<FilterRowState>,
  onlyReturnLineName: boolean = false,
  parentAttribute: string = "lineExits",
  relationValueAttribute: string = "line",
  dateChildAttribute: string = "exitTime"
): string | undefined => {
  const matchingRow = findRowByAttribute(filterGroup, parentAttribute);

  if (!matchingRow || !matchingRow.values || matchingRow.values.length === 0) {
    console.error(
      `no matching row found for "${parentAttribute}" or row has no values`
    );

    return;
  }

  // example expected format: [line=in:"Some Line",exitTime=inlast:1m]
  const valueString = matchingRow.values[0];

  try {
    // remove outer brackets if present
    const cleanedValue = valueString.replace(/^\[|\]$/g, "");

    // extract the relation value (e.g. line name)
    const valueMatch = cleanedValue.match(
      new RegExp(`${relationValueAttribute}=\\w+:"([^"]+)"`)
    );
    if (!valueMatch) {
      console.error(
        `no match for relationValueAttribute "${relationValueAttribute}" in filter value: ${cleanedValue}`
      );

      return;
    }

    const relationValue = valueMatch[1];

    if (onlyReturnLineName) {
      return relationValue;
    }

    return `${parentAttribute}.${relationValue}.${dateChildAttribute}`;
  } catch (error) {
    console.error("Error parsing advanced filter relation value:", error);

    return;
  }
};
